import * as React from 'react';
import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { CameraId, SiteId } from '@cguard/types';
import {
  getGetV1CamerasQueryKey,
  useGetV1Cameras,
  useGetV1CamerasId,
  type getV1CamerasIdResponse,
  type getV1CamerasResponse,
} from '../generated/endpoints/cameras/cameras';
import {
  CguardApiError,
  cguardFetcher,
  fetchAllPagedItems,
  getCguardClientConfig,
} from '../runtime/fetcher';
import { consumeSseStream } from '../realtime/sse-client';
import type { CameraSummary, ListCamerasParams } from '../view-models';

export type BrandedCameraSummary = Omit<CameraSummary, 'id' | 'siteId'> & {
  id: CameraId;
  siteId: SiteId;
  playbackConfigured: boolean;
  tier: string;
  vendor: string;
  model: string;
};

type CameraSummaryWire = Omit<Partial<CameraSummary>, 'status'> & {
  cameraId?: string | null;
  siteId?: string | null;
  vendor?: string | null;
  model?: string | null;
  status?: string | null;
  latencyMs?: number | null;
  thumbnailUrl?: string | null;
  playbackConfigured?: boolean | null;
  tier?: string | null;
};

/**
 * Normalize both known camera wire contracts:
 *
 * - MSW / stale OpenAPI stub: `{ id, name, siteId, status }`.
 * - Live central `CameraResponse`: `{ cameraId, siteId, vendor, model, status }`.
 *
 * The operator console keys WHEP streams and detection overlays by `id`, so
 * leaving the central shape un-normalized breaks the live demo while MSW still
 * passes.
 */
export function normaliseCameraSummary(row: CameraSummaryWire): BrandedCameraSummary {
  const id = (row.id ?? row.cameraId ?? '') as CameraId;
  const siteId = (row.siteId ?? '') as SiteId;
  const name = row.name ?? composeCameraName(row, id);

  return {
    ...row,
    id,
    siteId,
    name,
    status: normaliseCameraStatus(row.status),
    latencyMs: row.latencyMs ?? null,
    thumbnailUrl: row.thumbnailUrl ?? null,
    playbackConfigured: row.playbackConfigured ?? false,
    tier: row.tier ?? 'TierC',
    vendor: row.vendor ?? '',
    model: row.model ?? '',
  };
}

// `/v1/cameras` returns a `PagedResultOfCameraResponse` envelope
// (`{ items, page, pageSize, total, totalPages }`); the orval-generated type
// is stale and declares `data` as a flat array. Unwrap the envelope's
// `items` so the SPA always receives a real array.
const unwrapList = (res: getV1CamerasResponse): BrandedCameraSummary[] => {
  const body = res.data as unknown as CameraSummaryWire[] | { items?: CameraSummaryWire[] };
  if (Array.isArray(body)) return body.map(normaliseCameraSummary);
  return body?.items?.map(normaliseCameraSummary) ?? [];
};
const unwrapDetail = (res: getV1CamerasIdResponse): BrandedCameraSummary =>
  normaliseCameraSummary((res as { data?: CameraSummaryWire }).data ?? {});

function composeCameraName(row: CameraSummaryWire, id: CameraId): string {
  const vendor = row.vendor?.trim() ?? '';
  const model = row.model?.trim() ?? '';
  const displayName = `${vendor} ${model}`.trim();
  if (displayName.length > 0) return displayName;
  return id ? `Camera ${String(id).slice(0, 8)}` : 'Camera';
}

function normaliseCameraStatus(status: unknown): CameraSummary['status'] {
  const v = String(status ?? '')
    .trim()
    .toLowerCase();
  switch (v) {
    case 'active':
    case 'live':
    case 'online':
      return 'live';
    case 'registered':
    case 'pendingbind':
    case 'pending-bind':
    case 'discovered':
    case 'connecting':
    case 'degraded':
      return 'connecting';
    default:
      return 'offline';
  }
}

export function useCameraList(siteId?: SiteId): UseQueryResult<BrandedCameraSummary[], Error> {
  const params: ListCamerasParams = siteId ? { siteId: siteId as string } : {};
  return useGetV1Cameras<BrandedCameraSummary[], Error>(params, {
    query: { select: unwrapList },
  });
}

export function useAllCameraList(siteId?: SiteId): UseQueryResult<BrandedCameraSummary[], Error> {
  return useQuery({
    queryKey: [...getGetV1CamerasQueryKey(siteId ? { siteId } : undefined), 'all-pages'] as const,
    queryFn: () =>
      fetchAllPagedItems(
        async (page, pageSize) => {
          const response = await cguardFetcher<{
            data: unknown;
            status: number;
            headers: Headers;
          }>(
            `/v1/cameras?page=${page}&pageSize=${pageSize}${siteId ? `&siteId=${encodeURIComponent(siteId)}` : ''}`,
            { method: 'GET' },
          );
          return response.data;
        },
        (item) => normaliseCameraSummary(item as CameraSummaryWire),
      ),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
}

export function useCameraDetail(id: CameraId | null): UseQueryResult<BrandedCameraSummary, Error> {
  return useGetV1CamerasId<BrandedCameraSummary, Error>((id ?? '') as string, {
    query: { enabled: Boolean(id), select: unwrapDetail },
  });
}

/**
 * The pinned live-proof camera id, or `null` when not in live-proof mode.
 *
 * `VITE_LIVE_PROOF_CAMERA_ID` is read here (Vite inlines it at build time) so
 * both consoles resolve it identically. Empty/unset → `null` → normal roster.
 */
export function liveProofCameraId(): string | null {
  // The api-client lib's tsconfig has no `vite/client` augmentation of
  // `ImportMeta`, so reach `env` through the same cast the mock handlers use
  // (see mocks/handlers.ts) rather than depend on app-only ambient types.
  const meta = import.meta as ImportMeta & { env?: Record<string, string | undefined> };
  const raw = meta.env?.VITE_LIVE_PROOF_CAMERA_ID ?? '';
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Resolve the set of cameras the grid should render into its `tileCount` slots.
 *
 * Default (no live-proof pin): the historical behaviour — the first
 * `tileCount` cameras of the roster, one per tile.
 *
 * Live-proof pin set (`VITE_LIVE_PROOF_CAMERA_ID`, locked decision
 * "Real source on every tile", 2026-06-17): the grid collapses to the ONE
 * real edge-analyzed camera replicated across every tile. Because each tile
 * binds BOTH its WHEP video and its `detections` SSE boxes by that single
 * real camera UUID, what the browser shows is exactly the stream the edge
 * analyzed (modulo fixed pipeline latency) — no canned `cam-NN-ai` loops, no
 * synthetic tiles. Every tile is labelled identically: we never pretend the
 * tiles are distinct cameras.
 *
 * If the pinned id is not (yet) in the live roster we fall back to the normal
 * slice rather than fabricate a tile for a camera we cannot prove exists
 * (RULE #1 — never present a stream we don't have). `pinnedId` is injectable
 * for unit tests; in app code it defaults to {@link liveProofCameraId}.
 */
export function resolveGridRoster<T extends { id: string }>(
  cameras: ReadonlyArray<T>,
  tileCount: number,
  pinnedId: string | null = liveProofCameraId(),
): T[] {
  if (pinnedId) {
    const pinned = cameras.find((camera) => camera.id === pinnedId);
    if (pinned) return Array.from({ length: tileCount }, () => pinned);
  }
  return cameras.slice(0, tileCount);
}

// ─────────────────────────────────────────────────────────────────────────
// W8A — Camera Evidence (admin-console /cameras/{id} detail surface).
// These three endpoints are not in the OpenAPI tree (they ship in W8A and
// we don't churn the openapi-codegen pipeline mid-wave), so the hooks
// hand-roll fetch + EventSource against the configured base URL.
// ─────────────────────────────────────────────────────────────────────────

/** Response shape of `GET /v1/cameras/{id}/stream-token`. */
export interface CameraStreamToken {
  url: string;
  expiresAt: string;
  format: 'll-hls';
  token: string;
}

/**
 * Health-lite event payload pushed via SSE on
 * `GET /v1/cameras/{id}/health`. Mirrors the central
 * `CameraHealthLiteEvent` record. Stream diagnostics
 * (FPS / bitrate / codec / keyframe gap) are deliberately absent — the edge
 * publisher does not emit them today.
 */
export interface CameraHealthLiteEvent {
  status?: 'measured' | 'unknown' | 'confirmed' | 'recovered' | 'review';
  reason?: string | null;
  integrityClass?: string | null;
  sampleValid?: boolean;
  cameraId: string;
  observedAt: string;
  publishedAt?: string | null;
  brightnessMean?: number | null;
  contrast?: number | null;
  edgeDensity?: number | null;
  samplePeriodMs?: number | null;
  frameCount?: number | null;
  blackFrame?: boolean | null;
  whiteFrame?: boolean | null;
  tamperingActive?: boolean | null;
  frozenFrame?: boolean | null;
  sourceStalled?: boolean | null;
}

function joinUrl(path: string): string {
  const { baseUrl } = getCguardClientConfig();
  if (!baseUrl) return path;
  if (baseUrl.endsWith('/') && path.startsWith('/')) return baseUrl + path.slice(1);
  if (!baseUrl.endsWith('/') && !path.startsWith('/')) return `${baseUrl}/${path}`;
  return baseUrl + path;
}

function authHeader(): Record<string, string> {
  const { getAccessToken } = getCguardClientConfig();
  const token = getAccessToken?.();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Fetch a stream token for the given camera. The token is short-lived (60s);
 * the consuming route auto-refreshes via `refetchInterval` ~50s before expiry.
 */
export function useCameraStreamToken(
  cameraId: CameraId | null,
): UseQueryResult<CameraStreamToken, Error> {
  return useQuery<CameraStreamToken, Error>({
    queryKey: ['cameras', cameraId, 'stream-token'] as const,
    enabled: Boolean(cameraId),
    // 50s refetch — token expiry is 60s; we want a fresh token on the wire
    // before the edge enforcement layer rejects the next playlist fetch.
    refetchInterval: 50_000,
    refetchIntervalInBackground: false,
    staleTime: 0,
    queryFn: async (): Promise<CameraStreamToken> => {
      if (!cameraId) throw new Error('cameraId required');
      const res = await fetch(joinUrl(`/v1/cameras/${cameraId}/stream-token`), {
        headers: { Accept: 'application/json', ...authHeader() },
      });
      if (!res.ok) {
        let body: unknown = null;
        try {
          body = await res.json();
        } catch {
          /* body might not be JSON — ignore */
        }
        throw new CguardApiError(res.status, `stream-token failed: ${res.status}`, body);
      }
      return (await res.json()) as CameraStreamToken;
    },
  });
}

/**
 * Imperative snapshot fetcher (mutation-style). Returns a Blob URL the caller
 * is responsible for revoking when done. Backend rate-limits to 1 req/sec/camera.
 */
export function useCameraSnapshot(): UseMutationResult<
  { objectUrl: string; revoke: () => void },
  Error,
  { cameraId: CameraId }
> {
  return useMutation<{ objectUrl: string; revoke: () => void }, Error, { cameraId: CameraId }>({
    mutationFn: async ({ cameraId }): Promise<{ objectUrl: string; revoke: () => void }> => {
      const res = await fetch(joinUrl(`/v1/cameras/${cameraId}/snapshot`), {
        headers: { Accept: 'image/jpeg', ...authHeader() },
      });
      if (!res.ok) {
        let body: unknown = null;
        try {
          body = await res.json();
        } catch {
          /* ignore */
        }
        throw new CguardApiError(res.status, `snapshot failed: ${res.status}`, body);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      return { objectUrl, revoke: () => URL.revokeObjectURL(objectUrl) };
    },
  });
}

/**
 * Subscribe to the per-camera health-lite SSE stream. Returns the latest event,
 * a connection status flag, and the last error (if any).
 *
 * <p><b>W8A copilot-07 fixup #2.</b> The previous implementation used the
 * browser's native <code>EventSource</code> with an <code>?access_token=</code>
 * query parameter, which cannot authenticate against the central JwtBearer
 * stack — JwtBearer only reads <code>Authorization: Bearer ...</code> headers
 * (<code>JwtBearerExtensions.cs:29-45</code>). This rewrite consumes the SSE
 * stream via <code>fetch</code> + <code>ReadableStream</code> so we can carry a
 * real Bearer header, identical to the platform's primary realtime client at
 * <code>libs/ts/api-client/src/realtime/sse-client.ts</code>. The
 * <code>access_token</code> query fallback was deliberately removed; if the
 * caller has no token, the hook stays in the <code>error</code> state.</p>
 *
 * <p>Lifecycle: opens on mount when <code>cameraId</code> is non-null, aborts
 * on unmount or cameraId change. Auth failures (401/403) close the stream
 * permanently — there is no retry loop because that would mask a genuinely
 * unauthorised auditor session.</p>
 */
export function useCameraHealthLite(cameraId: CameraId | null): {
  latest: CameraHealthLiteEvent | null;
  status: 'idle' | 'connecting' | 'open' | 'closed' | 'error';
  error: Error | null;
} {
  const [latest, setLatest] = React.useState<CameraHealthLiteEvent | null>(null);
  const [status, setStatus] = React.useState<'idle' | 'connecting' | 'open' | 'closed' | 'error'>(
    'idle',
  );
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (!cameraId) {
      setStatus('idle');
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    setStatus('connecting');
    setError(null);

    const { getAccessToken } = getCguardClientConfig();
    const token = getAccessToken?.();
    if (!token) {
      // No token = no auditor session = do not even try to open the stream.
      // The endpoint requires cameras.evidence-view, so an unauthenticated
      // fetch would 401 immediately; failing fast keeps the UI honest.
      setError(new Error('No access token available for camera health-lite SSE.'));
      setStatus('error');
      return () => {
        cancelled = true;
      };
    }

    const run = async (): Promise<void> => {
      let response: Response;
      try {
        response = await fetch(joinUrl(`/v1/cameras/${cameraId}/health`), {
          method: 'GET',
          headers: {
            Accept: 'text/event-stream',
            'Cache-Control': 'no-cache',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
          signal: controller.signal,
          cache: 'no-store',
        });
      } catch (err) {
        if (cancelled || controller.signal.aborted) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setStatus('error');
        return;
      }

      if (!response.ok || response.body === null) {
        if (cancelled) return;
        setError(
          new CguardApiError(response.status, `health-lite SSE failed: ${response.status}`, null),
        );
        setStatus(response.status === 401 || response.status === 403 ? 'error' : 'closed');
        return;
      }

      setStatus('open');

      try {
        await consumeSseStream(response.body, (frame) => {
          if (frame.event !== 'health-lite' || frame.data.length === 0) return;
          try {
            const parsed = JSON.parse(frame.data) as CameraHealthLiteEvent;
            setLatest(parsed);
          } catch {
            // Malformed payloads are dropped silently — the SSE stream is
            // best-effort and the next heartbeat will replace this one.
          }
        });
        if (!cancelled) setStatus('closed');
      } catch (err) {
        if (cancelled || controller.signal.aborted) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setStatus('closed');
      }
    };

    void run();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [cameraId]);

  return { latest, status, error };
}
